import { env } from "env"
import { useContext, useEffect, PropsWithChildren } from "react"
import { sendTealiumEvent } from "tealium"
import { AuthContext } from "context/AuthContext"
import { ClientContext } from "context/ClientContext"
import { UserContext } from "context/UserContext"
import { ClientApi } from "api/client"
import {
  log,
  SHOW_LOGS,
  buildAuthRedirectUrl,
  getParamFromUrlHash,
  getSearchParams,
} from "utilities"

const ClientWrapper = ({ children }: PropsWithChildren) => {
  const { cid } = useContext(UserContext)
  const { adb2cToken, token } = useContext(AuthContext)
  const { client, setClient, isTealiumReady } = useContext(ClientContext)
  const urlHash = window.location.hash

  useEffect(() => {
    const initializeClient = async () => {
      if (!client && cid) {
        const res = await ClientApi.find({ cid })
        setClient(res)
      }
    }

    initializeClient()
  }, [cid, client, setClient])

  useEffect(() => {
    // If theres no tokens, go auth through ADB2C
    const idTokenFromHash = getParamFromUrlHash(urlHash, "id_token")
    // When ADB2C authentication fails, it redirects back with an error parameter
    const errorFromHash = getParamFromUrlHash(urlHash, "error")
    const redirectionUrl = env.DEV
      ? window.location.origin 
      : client?.redirectUrl

    // If there's an authentication error in the URL, don't redirect
    // This prevents redirect loops and allows ADB2C error pages to display properly
    if (errorFromHash) {
      return
    }

    if (
      client &&
      !token &&
      !adb2cToken &&
      !idTokenFromHash &&
      redirectionUrl &&
      client.adb2cUrl
    ) {
      // If Tealium isn't ready yet, wait for it
      if (!isTealiumReady) {
        return
      }

      const queryParams = getSearchParams()

      const finalUrl = buildAuthRedirectUrl({
        authServiceUrl: client.adb2cUrl,
        callbackUrl: redirectionUrl,
        params: queryParams,
      })

      sendTealiumEvent("app_auth_redirect", {
        type: "link",
        customer_id: client.cid,
        ...(queryParams.uid ? { uid: queryParams.uid } : {}),
        onSuccess: () => {
          window.location.replace(finalUrl)
        }
      }).catch(() => {
        // Redirect if Tealium fails
        window.location.replace(finalUrl)
      })
    }
  }, [token, adb2cToken, urlHash, client, isTealiumReady])

  if (!client) {
    log(SHOW_LOGS.CLIENT_ID, "Client not found, showing loader")
    return <></>
  }

  log(SHOW_LOGS.CLIENT_ID, `Client loaded: ${client.cid}`)
  return <>{children}</>
}

export default ClientWrapper
